import { PlusIcon as IconPlus } from '@persona-id/design-tokens/icons';
import { VFC } from 'react';
import styled from 'styled-components';

import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import FormError from 'dashboard/components/FormControls/FormError';
import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import Form from 'dashboard/layouts/FormLayout/Form';
import FormGroup from 'dashboard/layouts/FormLayout/FormGroup';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { ThemeSet } from 'dashboard/models/theme-set';
import { ThemeSetsFilterParams } from 'dashboard/redux/thunk-actions/theme-sets';
import ThemeSetsViewCard from 'dashboard/views/DashboardThemeSetsView/DashboardThemeSetsViewCard';
import ThemeSetsViewConfigurationBar from 'dashboard/views/DashboardThemeSetsView/DashboardThemeSetsViewConfigurationBar';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import useToggle from 'lib/hooks/useToggle';
import { useFormSecurely } from 'lib/security/reactHookForm';

const ThemeSetCards = styled('div')`
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  justify-content: space-around;
  grid-gap: 16px;
`;

interface DashboardThemeSetsViewProps {
  themeSets: ThemeSet[];
  indexFilter: ThemeSetsFilterParams;
  inquiryTemplates: InquiryTemplate[];
  createThemeSet: (themeSetName: string) => Promise<void>;
  cloneThemeSet: (themeSet: ThemeSet) => Promise<void>;
  archiveThemeSet: (themeSet: ThemeSet) => Promise<void>;
  updateOrgDefaultThemeSet: (themeSet: ThemeSet) => Promise<void>;
  setOmniSearch: (query: string) => void;
  setSortParams: (sortParam: SortParams) => void;
  isLoading: boolean;
}

const DashboardThemeSetsView = ({
  themeSets,
  indexFilter,
  inquiryTemplates,
  createThemeSet,
  cloneThemeSet,
  archiveThemeSet,
  updateOrgDefaultThemeSet,
  setOmniSearch,
  setSortParams,
  isLoading,
}: DashboardThemeSetsViewProps) => {
  const [
    shouldShowCreateThemeSetModal,
    { enable: showCreateThemeSetModal, disable: hideCreateThemeSetModal },
  ] = useToggle(false);

  return (
    <>
      <ModalWrapper
        isOpen={shouldShowCreateThemeSetModal}
        handleCloseModal={hideCreateThemeSetModal}
      >
        <CreateThemeSetModal
          handleClose={hideCreateThemeSetModal}
          createThemeSet={createThemeSet}
        />
      </ModalWrapper>
      <InactiveEnvironmentBanner />
      <Header title='Themes'>
        <Button
          id='themes-sets__add-themes-sets-button'
          onClick={showCreateThemeSetModal}
          width='auto'
          prefixIcon={<IconPlus />}
        >
          Create Theme
        </Button>
      </Header>
      <Container>
        <Section>
          <ThemeSetsViewConfigurationBar
            indexFilter={indexFilter}
            handleOmniUpdate={setOmniSearch}
            handleSortUpdate={setSortParams}
          />
          {isLoading && (
            <Container>
              <LoadingIndicator />
            </Container>
          )}
          {!isLoading && (
            <ThemeSetCards>
              {themeSets.map((themeSet) => (
                <ThemeSetsViewCard
                  key={themeSet.id}
                  themeSet={themeSet}
                  inquiryTemplates={inquiryTemplates}
                  onCloneThemeSet={cloneThemeSet}
                  onArchiveThemeSet={archiveThemeSet}
                  // Nothing needs to happen onClick so this is an empty callback, but a value is passed into
                  // `onClickThemeSetCard` to make sure the card has a hover state
                  onClickThemeSetCard={() => {}}
                  onUpdateOrgDefaultThemeSet={updateOrgDefaultThemeSet}
                  linkToEditor={true}
                />
              ))}
            </ThemeSetCards>
          )}
        </Section>
      </Container>
    </>
  );
};

interface CreateThemeSetModalProps {
  handleClose: () => void;
  createThemeSet: (themeSetName: string) => Promise<void>;
}

const CreateThemeSetModal: VFC<CreateThemeSetModalProps> = ({ handleClose, createThemeSet }) => {
  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
  } = useFormSecurely<{ name: string }>({
    defaultValues: {
      name: '',
    },
  });

  return (
    <ModalContainer id='theme-sets__new-theme-set-modal'>
      <ModalHeader onClose={handleClose}>New Theme</ModalHeader>
      <ModalBody>
        <Form
          onSubmit={handleSubmit((data) => {
            createThemeSet(data.name);
            handleClose();
          })}
        >
          <FormGroup>
            <FormControlLabel htmlFor='theme-sets__new-theme-set-modal--name'>
              Name
            </FormControlLabel>
            <Input
              id='theme-sets__new-theme-set-modal--name'
              hasError={!!errors.name}
              {...register('name', {
                required: 'Please give your Theme a name',
                maxLength: { value: 255, message: 'Theme name must not exceed 255 characters' },
              })}
            />
            {errors.name && <FormError>{errors.name.message}</FormError>}
            <Spacer height={2} />
          </FormGroup>

          <Button
            type='submit'
            width='auto'
            id='theme-sets__new-theme-set-modal--btn-continue'
            isPending={isSubmitting}
            disabled={errors?.name != null}
            onClick={handleSubmit((data) => {
              createThemeSet(data.name);
              handleClose();
            })}
          >
            Create
          </Button>
        </Form>
      </ModalBody>
    </ModalContainer>
  );
};

export default DashboardThemeSetsView;
